import { DetailSectionTemplate } from "components/MainSectionTemplate/DetailSectionTemplate";
import { RenderEditOptions } from "components/MainSectionTemplate/types";
import RequiredTextInput from "components/ValidatedFields/RequiredTextInput";
import React, { useState } from "react";
import { ICalendarTemplateInfo } from "./models";
import RequiredTextInputWithMergeField from "components/ValidatedFields/RequiredTextInputWithMergeField";
import OptionalTextAreaInputWithMergeField from "components/ValidatedFields/OptionalTextAreaInputWithMergeField";
import { replaceNewLineWithBr } from "utilities/reactUtils";
import { nounToLower } from "utilities/translationUtils";

interface ICalendarTemplateDetailSectionProps {
  iCalendarTemplate: server.dto.ICalendarTemplate
  availableMergeFieldTopics: server.dto.AvailableMergeFieldTopic[]
}

export const ICalendarTemplateDetailSection = (props: ICalendarTemplateDetailSectionProps) => {
  const [iCalendarTemplate, setICalendarTemplate] = useState<ICalendarTemplateInfo>({
    title: props.iCalendarTemplate.title,
    subject: props.iCalendarTemplate.subject,
    body: props.iCalendarTemplate.body,
  });

  const onValueChanged = (key: string, value: any) => {
    if (key === "title") {
      setICalendarTemplate({ ...iCalendarTemplate, title: value });
    } else if (key === "subject") {
      setICalendarTemplate({ ...iCalendarTemplate, subject: value });
    } else if (key === "body") {
      setICalendarTemplate({ ...iCalendarTemplate, body: value });
    }
  }

  const getUpdateICalendarTemplateInstruction = (ical: ICalendarTemplateInfo): server.dto.UpdateICalendarTemplateInstruction => ({
    iCalendarTemplateId: props.iCalendarTemplate.iCalendarTemplateId,
    title: ical.title,
    subject: ical.subject,
    body: ical.body,
  });

  return <DetailSectionTemplate
    isInPageEditable
    onValueChanged={onValueChanged}
    sections={[{
      type: "regular",
      key: "iCalendarTemplate",
      title: RESX.Template.ICalendarTemplate,
      fields: [{
        key: "title",
        label: RESX.GeneralLabels.Title,
        render: () => <div>{iCalendarTemplate.title}</div>,
        inPageEdit: {
          render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <RequiredTextInput
            validatorKey="title"
            focusOnMount
            maxLength={serverReference.constants.iCalendarTemplate.maxTitleLength}
            value={value}
            onChange={(title) => { onChange(title); }}
            onEnter={() => submit()}
            onEsc={() => cancel()}
          />,
          fullWidth: true,
          type: "confirmOrCancelButton",
          initialValue: iCalendarTemplate.title,
          submitChange: (title: string) => window.CoreApi.ICalendarTemplate.Update({
            ...getUpdateICalendarTemplateInstruction(iCalendarTemplate),
            title,
          }),
        },
      }, {
        key: "subject",
        label: RESX.Message.Subject,
        render: () => <div>{iCalendarTemplate.subject}</div>,
        inPageEdit: {
          render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <RequiredTextInputWithMergeField
            validatorKey="subject"
            focusOnMount
            maxLength={serverReference.constants.iCalendarTemplate.maxSubjectLength}
            value={value}
            onChange={(subject) => { onChange(subject); }}
            onEnter={() => submit()}
            onEsc={() => cancel()}
            sectionName={nounToLower(site.currentEnvironment.show.singular)}
            availableMergeFieldTopics={props.availableMergeFieldTopics}
          />,
          fullWidth: true,
          type: "confirmOrCancelButton",
          initialValue: iCalendarTemplate.subject,
          submitChange: (subject: string) => window.CoreApi.ICalendarTemplate.Update({
            ...getUpdateICalendarTemplateInstruction(iCalendarTemplate),
            subject,
          }),
        },
      }, {
        key: "body",
        label: RESX.Message.Body,
        isEmpty: !iCalendarTemplate.body,
        render: () => <div>{replaceNewLineWithBr(iCalendarTemplate.body)}</div>,
        inPageEdit: {
          render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextAreaInputWithMergeField
            focusOnMount
            maxLength={serverReference.constants.iCalendarTemplate.maxBodyLength}
            value={value}
            onChange={(body) => { onChange(body); }}
            onCtrlEnter={() => submit()}
            onEsc={() => cancel()}
            rows={30}
            sectionName={nounToLower(site.currentEnvironment.show.singular)}
            availableMergeFieldTopics={props.availableMergeFieldTopics}
          />,
          fullWidth: true,
          type: "confirmOrCancelButton",
          initialValue: iCalendarTemplate.body,
          submitChange: (body: string) => window.CoreApi.ICalendarTemplate.Update({
            ...getUpdateICalendarTemplateInstruction(iCalendarTemplate),
            body,
          }),
        },
      }]
    }]}
  />
}